import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@clerk/clerk-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { createApiClient } from "../../lib/api/client";
import type { DeltaStatic } from "quill";
import type { CreateFeedbackRequest, Feedback } from "../../services/feedback";
import { TypingAnimation } from "./TypingAnimation";
import { StreamingMarkdownRenderer } from "./StreamingMarkdownRenderer";
import { Brain } from "lucide-react";

// -----------------------------
// Types
// -----------------------------
interface RagSource {
  id: string;
  title?: string;
  score: number;
  content?: string;
  formatted_content?: DeltaStatic;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  sources?: RagSource[];
  timestamp: number;
  conversationId?: string;
  messageIndex?: number;
  isStreaming?: boolean;
  isHistorical?: boolean; // Flag to indicate if message is from chat history (no typewriter effect)
  isConversationNotice?: boolean; // Flag to indicate this is a conversation continuation notice
  isUnanswered?: boolean; // Flag to indicate this user question could not be answered
}

interface ChatMessageProps {
  message: ChatMessage;
  onDocumentClick?: (doc: RagSource) => void;
}

// -----------------------------
// Markdown Renderer
// -----------------------------
function MarkdownRenderer({ content }: { content: string }) {
  return (
    <div className="prose prose-sm max-w-none prose-strong:text-brand-text prose-em:text-brand-text prose-table:text-xs prose-th:p-2 prose-td:p-2 prose-table:border-collapse prose-table:border prose-th:border prose-td:border prose-th:border-brand-border prose-td:border-brand-border prose-table:border-brand-border">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          code: ({ children }) => <>{children}</>,
          pre: ({ children }) => <span className="whitespace-pre-wrap">{children}</span>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

// -----------------------------
// ChatMessage Component
// -----------------------------
export function ChatMessage({ message, onDocumentClick }: ChatMessageProps) {
  const { getToken } = useAuth();
  const api = createApiClient(getToken);

  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackType, setFeedbackType] = useState<
    "positive" | "negative" | null
  >(null);
  const [content, setContent] = useState(message.content);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");

  const feedbackReasons = [
    "Réponse incorrecte ou trompeuse",
    "Information manquante ou incomplète",
    "Réponse hors sujet",
    "Format de réponse difficile à comprendre",
    "Sources non pertinentes",
    "Autre",
  ];

  useEffect(() => {
    setFeedbackSubmitted(false);
    setFeedbackType(null);
    setShowFeedbackModal(false);
    setSelectedReason("");
    setCustomReason("");
  }, [message.conversationId, message.messageIndex]);

  const handlePositiveFeedback = async () => {
    if (
      !message.conversationId ||
      message.messageIndex === undefined ||
      feedbackSubmitted
    )
      return;
    try {
      const feedback: CreateFeedbackRequest = {
        conversation_id: message.conversationId,
        message_index: message.messageIndex,
        rating: "up",
        reason: "",
      };
      await api.post("/feedback", feedback);
      setFeedbackType("positive");
      setFeedbackSubmitted(true);
    } catch (err) {
      console.error("Feedback error:", err);
    }
  };

  const handleNegativeFeedback = () => {
    if (
      !message.conversationId ||
      message.messageIndex === undefined ||
      feedbackSubmitted
    )
      return;
    setShowFeedbackModal(true);
  };

  const submitNegativeFeedback = async () => {
    if (
      !message.conversationId ||
      message.messageIndex === undefined ||
      feedbackSubmitted
    )
      return;
    const finalReason =
      selectedReason === "Autre" ? customReason : selectedReason;
    try {
      const feedback: CreateFeedbackRequest = {
        conversation_id: message.conversationId,
        message_index: message.messageIndex,
        rating: "down",
        reason: finalReason,
      };
      await api.post("/feedback", feedback);
      setFeedbackType("negative");
      setFeedbackSubmitted(true);
      setShowFeedbackModal(false);
    } catch (err) {
      console.error("Feedback error:", err);
    }
  };

  const formatTime = (ts: number) =>
    new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const onChunk = (chunk: string) => {
    console.log("Received chunk:", {
      chunk: chunk,
      repr: JSON.stringify(chunk),
      length: chunk.length,
      isEmpty: chunk === "",
      isWhitespace: chunk.trim() === "",
    });
    setContent((prev) => prev + chunk);
  };

  // Special rendering for conversation notices
  if (message.isConversationNotice) {
    return (
      <div className="flex justify-center mb-4">
        <div className="max-w-[80%] rounded-lg px-4 py-2 bg-brand-surface border border-brand-border">
          <div className="flex items-center gap-2 text-brand-muted text-sm justify-center">
            <Brain className="w-4 h-4" />
            <div className="italic">
              <MarkdownRenderer content={message.content} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`flex ${
          message.role === "user" ? "justify-end" : "justify-start"
        } mb-4`}
      >
        <div
          className={`max-w-[80%] rounded-lg px-4 py-3 ${
            message.role === "user"
              ? message.isUnanswered
                ? "bg-brand-warning text-brand-surface border-2 border-brand-warning"
                : "bg-brand-primary text-brand-surface"
              : "bg-brand-bg shadow-card rounded-card text-brand-text text-left"
          }`}
        >
          {message.role === "user" ? (
            <div className="whitespace-pre-wrap text-left">
              {message.content}
              {message.isUnanswered && (
                <span className="block text-sm text-brand-surface mt-3 pt-2 border-t border-brand-warning/30">
                  Question sans réponse
                </span>
              )}
            </div>
          ) : message.content.trim() === "" ? (
            <TypingAnimation className="py-2" />
          ) : message.isHistorical ? (
            // Historical messages: render immediately without typewriter effect
            <MarkdownRenderer content={message.content} />
          ) : (
            // New messages: use typewriter effect
            <StreamingMarkdownRenderer
              content={message.content}
              isStreaming={message.isStreaming || false}
              showCursor={message.isStreaming || false}
              typewriterSpeed={15} // Fast typewriter for real-time feel
            />
          )}

          {/* Uncomment to show sources */}
          {/* {message.role === "assistant" && message.sources?.length > 0 && (
            <div className="mt-4 border-t border-brand-border pt-3">
              <div className="text-xs text-brand-muted mb-2 font-semibold">Sources:</div>
              {message.sources.map(source => (
                <div
                  key={source.id}
                  className="text-xs bg-brand-surface rounded p-2 cursor-pointer hover:bg-brand-surface transition"
                  onClick={() => onDocumentClick?.(source)}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-brand-teal truncate mr-2">
                      {source.title || `Document ${source.id}`}
                    </span>
                    <span className="text-brand-muted flex-shrink-0">
                      Score: {Math.round(source.score * 100)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )} */}

          {message.role === "assistant" &&
            message.conversationId &&
            message.messageIndex !== undefined &&
            !message.isStreaming &&
            message.content.trim() !== "" && (
              <div className="flex justify-between items-center mt-3 pt-2 border-t border-brand-border">
                <div className="flex items-center gap-2">
                  {feedbackSubmitted ? (
                    <span className="text-xs text-brand-teal">
                      Feedback enregistré
                    </span>
                  ) : (
                    <>
                      <span className="text-xs text-brand-muted">Utile ?</span>
                      <button
                        onClick={handlePositiveFeedback}
                        disabled={feedbackSubmitted}
                        className={`p-1 rounded ${
                          feedbackType === "positive"
                            ? "text-brand-text bg-brand-surface"
                            : "text-brand-muted hover:text-brand-text hover:bg-brand-surface"
                        } disabled:opacity-50`}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                        </svg>
                      </button>
                      <button
                        onClick={handleNegativeFeedback}
                        disabled={feedbackSubmitted}
                        className={`p-1 rounded ${
                          feedbackType === "negative"
                            ? "text-brand-text bg-brand-surface"
                            : "text-brand-muted hover:text-brand-text hover:bg-brand-surface"
                        } disabled:opacity-50`}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.106-1.79l-.05-.025A4 4 0 0011.057 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
                <span className="text-xs text-brand-muted">
                  {formatTime(message.timestamp)}
                </span>
              </div>
            )}
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-brand-bg rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-brand-text mb-4">
              Pourquoi cette réponse n'était-elle pas utile ?
            </h3>
            <div className="space-y-2 mb-6">
              {feedbackReasons.map((reason) => (
                <label key={reason} className="flex items-center text-brand-text">
                  <input
                    type="radio"
                    name="feedbackReason"
                    value={reason}
                    checked={selectedReason === reason}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="mr-3 text-brand-primary"
                  />
                  <span className="text-sm">{reason}</span>
                </label>
              ))}
              {selectedReason === "Autre" && (
                <div className="mt-4">
                  <label className="block text-sm text-brand-muted mb-2">
                    Précisez votre raison (max 140 caractères):
                  </label>
                  <textarea
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    maxLength={140}
                    placeholder="Décrivez votre raison..."
                    className="w-full p-3 bg-brand-surface text-brand-text rounded border border-brand-border focus:border-brand-primary focus:outline-none resize-none"
                    rows={3}
                  />
                  <div className="text-xs text-brand-muted mt-1 text-right">
                    {customReason.length}/140 caractères
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowFeedbackModal(false)}
                className="flex-1 px-4 py-2 bg-brand-surface text-brand-text rounded hover:opacity-90 transition"
              >
                Annuler
              </button>
              <button
                onClick={submitNegativeFeedback}
                disabled={
                  !selectedReason ||
                  (selectedReason === "Autre" && !customReason.trim())
                }
                className="flex-1 px-4 py-2 bg-brand-primary text-brand-surface rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
